import { logger } from './config'
import { getLogContext, updateLogContext } from './context'
import * as Sentry from '@sentry/nextjs'

export class ApiLogger {
  private static log(level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: any) {
    try {
      const context = getLogContext()
      const logData = {
        ...context,
        ...data,
        msg: message,
        timestamp: new Date().toISOString(),
      }

      // Log to Pino (structured logging)
      logger[level](logData)

      // Also send to Sentry for monitoring
      this.sendToSentry(level, message, logData)
    } catch (error) {
      // Fallback to console if logger fails
      console[level === 'debug' ? 'log' : level](`[ApiLogger] ${message}`, data)
    }
  }

  private static sendToSentry(level: 'info' | 'warn' | 'error' | 'debug', message: string, data: any) {
    try {
      // Send different log levels to Sentry appropriately
      switch (level) {
        case 'error':
          // Capture as exception if there's an error object
          if (data?.error instanceof Error) {
            Sentry.captureException(data.error, {
              extra: { ...data, originalMessage: message },
              level: 'error'
            })
          } else {
            Sentry.captureMessage(message, 'error')
            Sentry.addBreadcrumb({
              message,
              category: 'log',
              level: 'error',
              data
            })
          }
          break
        case 'warn':
          Sentry.captureMessage(message, 'warning')
          Sentry.addBreadcrumb({
            message,
            category: 'log',
            level: 'warning',
            data
          })
          break
        case 'info':
          // Only add breadcrumb for info logs to avoid noise
          Sentry.addBreadcrumb({
            message,
            category: 'log',
            level: 'info',
            data
          })
          break
        case 'debug':
          // Skip debug logs in Sentry to reduce noise
          break
      }
    } catch (sentryError) {
      // Don't let Sentry failures break logging
      console.warn('Failed to send log to Sentry:', sentryError)
    }
  }

  static info(message: string, data?: any) {
    this.log('info', message, data)
  }

  static warn(message: string, data?: any) {
    this.log('warn', message, data)
  }

  static error(message: string, data?: any) {
    this.log('error', message, data)
  }

  static debug(message: string, data?: any) {
    this.log('debug', message, data)
  }

  static requestStarted(method: string, path: string, data?: any) {
    this.info('Request started', {
      method,
      path,
      ...data,
    })
  }

  static requestCompleted(statusCode: number, duration: number, data?: any) {
    this.info('Request completed', {
      statusCode,
      duration,
      ...data,
    })
  }

  static requestFailed(error: Error, statusCode: number, data?: any) {
    this.error('Request failed', {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      statusCode,
      ...data,
    })
  }

  static setUserId(userId: string) {
    updateLogContext({ userId })

    // Also update Sentry user context
    try {
      Sentry.setUser({ id: userId })
    } catch (error) {
      console.warn('Failed to set Sentry user ID:', error)
    }
  }
}

// Export logger instance for direct use when needed
export { logger }
